import React, { useState } from "react";
import { useAuth, buildConsentRecord } from "../context/AuthContext";
import {
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  X,
  Loader,
  ArrowLeft,
} from "lucide-react";

const AuthModal = ({ onClose, onSuccess }) => {
  const [mode, setMode] = useState("login"); // 'login', 'signup', 'forgot'
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    displayName: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [agreed, setAgreed] = useState(false); // 18+ and Terms/Privacy (signup)

  const {
    signUp,
    signIn,
    signInWithGoogle,
    resetPassword,
    sendVerificationEmail,
  } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    try {
      if (mode === "signup") {
        // Consent + age confirmation (required before any account is created)
        if (!agreed) {
          setError(
            "Please confirm you are 18 or older and accept the Terms and Privacy Policy.",
          );
          setLoading(false);
          return;
        }

        // Validation
        if (formData.password !== formData.confirmPassword) {
          setError("Passwords do not match");
          setLoading(false);
          return;
        }

        if (formData.password.length < 6) {
          setError("Password must be at least 6 characters");
          setLoading(false);
          return;
        }

        if (!formData.displayName.trim()) {
          setError("Please enter your name");
          setLoading(false);
          return;
        }

        // Sign up
        // Sign up
        const newUser = await signUp(
          formData.email,
          formData.password,
          formData.displayName,
          buildConsentRecord("checkbox"),
        );
        // ❌ REMOVE THIS LINE: onSuccess(true);

        // Send verification email
        try {
          await sendVerificationEmail(newUser);
          setMessage(
            "✅ Account created! Verification email sent to " +
              formData.email +
              ". Please check your inbox (including spam folder).",
          );
          setLoading(false);

          setTimeout(() => {
            onSuccess(true);
          }, 4000);
        } catch (verifyError) {
          console.error("Verification email error:", verifyError);

          // Even if email fails, account is created
          setMessage(
            "✅ Account created successfully! However, we couldn't send the verification email. You can still login and request a new verification email later.",
          );
          setLoading(false);

          setTimeout(() => {
            onSuccess(true);
          }, 4000);
        }
      } else if (mode === "login") {
        // Login
        const user = await signIn(formData.email, formData.password);

        // Check if email is verifiedLine 98:   // Check if email is verified
        if (!user.emailVerified) {
          setMessage(
            "⚠️ Please verify your email. Check your inbox for verification link.",
          );
          setLoading(false);

          // Show message for 2 seconds
          setTimeout(() => {
            onSuccess(false);
          }, 2000);
        } else {
          // Email verified, close immediately
          onSuccess(false);
        }
      } else if (mode === "forgot") {
        // Send password reset email
        await resetPassword(formData.email);
        setMessage("✅ Password reset email sent! Check your inbox.");
        setLoading(false);
        return;
      }
    } catch (err) {
      // Handle Firebase errors
      if (err.code === "auth/email-already-in-use") {
        setError("This email is already registered. Please login.");
      } else if (err.code === "auth/invalid-email") {
        setError("Invalid email address");
      } else if (err.code === "auth/user-not-found") {
        setError("No account found with this email");
      } else if (err.code === "auth/wrong-password") {
        setError("Incorrect password");
      } else if (err.code === "auth/weak-password") {
        setError("Password is too weak");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(err.message || "An error occurred. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setMessage("");

    if (mode === "signup" && !agreed) {
      setError(
        "Please confirm you are 18 or older and accept the Terms and Privacy Policy.",
      );
      return;
    }

    setLoading(true);

    try {
      // Signup tab: explicit checkbox. Login tab: visible notice under the button.
      const result = await signInWithGoogle(
        buildConsentRecord(mode === "signup" ? "checkbox" : "notice"),
      );
      // Google accounts are automatically verified
      const isNewUser = result.additionalUserInfo?.isNewUser || false;
      onSuccess(isNewUser);
    } catch (err) {
      setError("Failed to sign in with Google. Please try again.");
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError("");
    setMessage("");
    setFormData({
      email: formData.email, // Keep email when switching
      password: "",
      displayName: "",
      confirmPassword: "",
    });
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "2rem",
        fontFamily: '"DM Sans", sans-serif',
        animation: "fadeIn 0.3s ease-in-out",
      }}
    >
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: "24px",
          padding: "3rem",
          maxWidth: "450px",
          width: "100%",
          position: "relative",
          color: "#fff",
          animation: "slideUp 0.3s ease-in-out",
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "1rem",
            right: "1rem",
            background: "rgba(255,255,255,0.2)",
            border: "none",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#fff",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.target.style.background = "rgba(255,255,255,0.3)")
          }
          onMouseLeave={(e) =>
            (e.target.style.background = "rgba(255,255,255,0.2)")
          }
        >
          <X size={20} />
        </button>

        {/* Back button for forgot password */}
        {mode === "forgot" && (
          <button
            onClick={() => switchMode("login")}
            style={{
              position: "absolute",
              top: "1rem",
              left: "1rem",
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#fff",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.2)")
            }
          >
            <ArrowLeft size={20} />
          </button>
        )}

        {/* Header */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "2rem",
            marginTop: mode === "forgot" ? "1rem" : 0,
          }}
        >
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: "800",
              marginBottom: "0.5rem",
            }}
          >
            {mode === "login"
              ? "Welcome Back!"
              : mode === "signup"
                ? "Join NORYQ"
                : "Reset Password"}
          </h2>
          <p style={{ opacity: 0.9, fontSize: "0.95rem" }}>
            {mode === "login"
              ? "Sign in to continue your journey"
              : mode === "signup"
                ? "Create an account to get personalized recommendations"
                : "Enter your email to receive reset instructions"}
          </p>
        </div>

        {/* Consent + age confirmation (signup only; covers email AND Google) */}
        {mode === "signup" && (
          <label
            style={{
              display: "flex",
              gap: "0.6rem",
              alignItems: "flex-start",
              marginBottom: "1.25rem",
              fontSize: "0.8125rem",
              lineHeight: 1.5,
              cursor: "pointer",
              background: "rgba(255,255,255,0.12)",
              padding: "0.75rem",
              borderRadius: "12px",
            }}
          >
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }}
            />
            <span>
              I am 18 years or older, and I have read and agree to the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#fff", textDecoration: "underline" }}
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#fff", textDecoration: "underline" }}
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>
        )}

        {/* Google Sign In - Only for login/signup */}
        {mode !== "forgot" && (
          <>
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              style={{
                width: "100%",
                padding: "1rem",
                background: "#fff",
                color: "#667eea",
                border: "none",
                borderRadius: "12px",
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: "600",
                fontSize: "1rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.75rem",
                marginBottom: "1.5rem",
                transition: "transform 0.2s",
                opacity: loading ? 0.7 : 1,
              }}
              onMouseEnter={(e) =>
                !loading && (e.target.style.transform = "translateY(-2px)")
              }
              onMouseLeave={(e) => (e.target.style.transform = "translateY(0)")}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {loading ? "Signing in..." : "Continue with Google"}
            </button>

            {mode === "login" && (
              <p
                style={{
                  fontSize: "0.75rem",
                  opacity: 0.8,
                  textAlign: "center",
                  margin: "-0.5rem 0 1rem",
                  lineHeight: 1.5,
                }}
              >
                New here? By continuing with Google you confirm you are 18+ and
                agree to our{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#fff", textDecoration: "underline" }}
                >
                  Terms
                </a>{" "}
                and{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#fff", textDecoration: "underline" }}
                >
                  Privacy Policy
                </a>
                .
              </p>
            )}

            {/* Divider */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                marginBottom: "1.5rem",
              }}
            >
              <div
                style={{
                  flex: 1,
                  height: "1px",
                  background: "rgba(255,255,255,0.3)",
                }}
              />
              <span style={{ opacity: 0.7, fontSize: "0.875rem" }}>or</span>
              <div
                style={{
                  flex: 1,
                  height: "1px",
                  background: "rgba(255,255,255,0.3)",
                }}
              />
            </div>
          </>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Name field (signup only) */}
          {mode === "signup" && (
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                }}
              >
                Full Name
              </label>
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <User
                  size={18}
                  style={{
                    position: "absolute",
                    left: "1rem",
                    opacity: 0.7,
                  }}
                />
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  required
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: "0.875rem 1rem 0.875rem 3rem",
                    background: "rgba(255,255,255,0.9)",
                    border: "none",
                    borderRadius: "12px",
                    fontSize: "1rem",
                    color: "#333",
                    outline: "none",
                  }}
                />
              </div>
            </div>
          )}

          {/* Email field */}
          <div style={{ marginBottom: "1rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontSize: "0.875rem",
                fontWeight: "600",
              }}
            >
              Email
            </label>
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Mail
                size={18}
                style={{
                  position: "absolute",
                  left: "1rem",
                  opacity: 0.7,
                }}
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "0.875rem 1rem 0.875rem 3rem",
                  background: "rgba(255,255,255,0.9)",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "1rem",
                  color: "#333",
                  outline: "none",
                }}
              />
            </div>
          </div>

          {/* Password field (not for forgot password) */}
          {mode !== "forgot" && (
            <div
              style={{ marginBottom: mode === "signup" ? "1rem" : "0.5rem" }}
            >
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                }}
              >
                Password
              </label>
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Lock
                  size={18}
                  style={{
                    position: "absolute",
                    left: "1rem",
                    opacity: 0.7,
                  }}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: "0.875rem 3rem 0.875rem 3rem",
                    background: "rgba(255,255,255,0.9)",
                    border: "none",
                    borderRadius: "12px",
                    fontSize: "1rem",
                    color: "#333",
                    outline: "none",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "1rem",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {showPassword ? (
                    <EyeOff size={18} color="#666" />
                  ) : (
                    <Eye size={18} color="#666" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Forgot password link (login only) */}
          {mode === "login" && (
            <div style={{ textAlign: "right", marginBottom: "1.5rem" }}>
              <button
                type="button"
                onClick={() => switchMode("forgot")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  textDecoration: "underline",
                  opacity: 0.9,
                }}
              >
                Forgot password?
              </button>
            </div>
          )}

          {/* Confirm Password (signup only) */}
          {mode === "signup" && (
            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                }}
              >
                Confirm Password
              </label>
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Lock
                  size={18}
                  style={{
                    position: "absolute",
                    left: "1rem",
                    opacity: 0.7,
                  }}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  required
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: "0.875rem 1rem 0.875rem 3rem",
                    background: "rgba(255,255,255,0.9)",
                    border: "none",
                    borderRadius: "12px",
                    fontSize: "1rem",
                    color: "#333",
                    outline: "none",
                  }}
                />
              </div>
            </div>
          )}

          {/* Success message */}
          {message && (
            <div
              style={{
                background: "rgba(76,175,80,0.2)",
                padding: "0.875rem",
                borderRadius: "12px",
                marginBottom: "1.5rem",
                fontSize: "0.875rem",
                border: "1px solid rgba(76,175,80,0.3)",
              }}
            >
              {message}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div
              style={{
                background: "rgba(255,0,0,0.2)",
                padding: "0.875rem",
                borderRadius: "12px",
                marginBottom: "1.5rem",
                fontSize: "0.875rem",
                border: "1px solid rgba(255,0,0,0.3)",
              }}
            >
              {error}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "1rem",
              background: "#fff",
              color: "#667eea",
              border: "none",
              borderRadius: "12px",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: "700",
              fontSize: "1rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              transition: "transform 0.2s",
              opacity: loading ? 0.7 : 1,
            }}
            onMouseEnter={(e) =>
              !loading && (e.target.style.transform = "translateY(-2px)")
            }
            onMouseLeave={(e) => (e.target.style.transform = "translateY(0)")}
          >
            {loading && (
              <Loader
                size={18}
                style={{ animation: "spin 1s linear infinite" }}
              />
            )}
            {loading
              ? "Please wait..."
              : mode === "login"
                ? "Sign In"
                : mode === "signup"
                  ? "Create Account"
                  : "Send Reset Link"}
          </button>
        </form>

        {/* Switch mode */}
        {mode !== "forgot" && (
          <div
            style={{
              textAlign: "center",
              marginTop: "1.5rem",
              fontSize: "0.875rem",
            }}
          >
            {mode === "login" ? (
              <p>
                Don't have an account?{" "}
                <button
                  onClick={() => switchMode("signup")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#fff",
                    fontWeight: "700",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "0.875rem",
                  }}
                >
                  Sign up
                </button>
              </p>
            ) : (
              <p>
                Already have an account?{" "}
                <button
                  onClick={() => switchMode("login")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#fff",
                    fontWeight: "700",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "0.875rem",
                  }}
                >
                  Sign in
                </button>
              </p>
            )}
          </div>
        )}

        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default AuthModal;
